import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DotsHorizontalIcon,
} from '@radix-ui/react-icons';

import { cn } from '@/shared/utils/cn';

interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const PaginationBar = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationBarProps) => {
  if (totalPages <= 1) return null;

  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className="flex w-full justify-end py-6"
    >
      <ul className="flex flex-row items-center gap-1">
        {currentPage > 1 && (
          <li>
            <button
              aria-label="Go to previous page"
              className={cn(pageButtonClass, 'gap-1 pl-2.5 pr-3')}
              onClick={() => onPageChange(currentPage - 1)}
            >
              <ChevronLeftIcon className="size-4" />
            </button>
          </li>
        )}

        {currentPage > 2 && (
          <li>
            <span
              aria-hidden
              className="flex size-9 items-center justify-center"
            >
              <DotsHorizontalIcon className="size-4" />
            </span>
          </li>
        )}

        {currentPage > 1 && (
          <li>
            <button
              className={pageButtonClass}
              onClick={() => onPageChange(currentPage - 1)}
            >
              {currentPage - 1}
            </button>
          </li>
        )}

        <li>
          <button
            aria-current="page"
            className={cn(pageButtonClass, 'rounded-sm bg-white/10')}
          >
            {currentPage}
          </button>
        </li>

        {currentPage < totalPages && (
          <li>
            <button
              className={pageButtonClass}
              onClick={() => onPageChange(currentPage + 1)}
            >
              {currentPage + 1}
            </button>
          </li>
        )}

        {currentPage + 1 < totalPages && (
          <li>
            <span
              aria-hidden
              className="flex size-9 items-center justify-center"
            >
              <DotsHorizontalIcon className="size-4" />
            </span>
          </li>
        )}

        {currentPage < totalPages && (
          <li>
            <button
              aria-label="Go to next page"
              className={cn(pageButtonClass, 'gap-1 pl-3 pr-2.5')}
              onClick={() => onPageChange(currentPage + 1)}
            >
              <ChevronRightIcon className="size-4" />
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
};

const pageButtonClass =
  'inline-flex size-9 items-center justify-center rounded-sm text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
